import { 
  Box, 
  Chip, 
  IconButton, 
  Pagination, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Tooltip, 
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Button,
  InputAdornment
} from "@mui/material";
import { formatDate, getSupportCategoryItemById } from "../../../../utils/helpers";
import { AdminPanelSettings, Visibility, Search, FilterList, Clear } from "@mui/icons-material";
import { Ticket, TicketDetails, typeResponse } from "../../../../utils/types";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import instance from "../../../../providers/axios.config";
import SupportTicketDetails from "./SupportTicketDetails";
import { RootState } from "../../../../redux/reducers/rootReducer";
import { useSelector } from "react-redux";
import { supportCategoryTypes } from "../../../../utils/constants";
import { useLocation, useNavigate } from "react-router-dom";

interface TicketFilters {
  status: string;
  priority: string;
  category: string;
  search: string;
}

const SupportTickersList = () => {
  const { t } = useTranslation()
  const location = useLocation();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [ticketsPage, setTicketsPage] = useState(1)
  const [ticketsTotal, setTicketsTotal] = useState(0)
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedTicket, setSelectedTicket] = useState<TicketDetails | null>(null)
  const [showFilters, setShowFilters] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  
  const [filters, setFilters] = useState<TicketFilters>({
    status: 'all',
    priority: 'all',
    category: 'all',
    search: ''
  });

  const account = useSelector((state: RootState) => state.user.usersData)

  // Available filter options
  const statusOptions = [
    { value: 'all', label: t('all_statuses') },
    { value: 'open', label: t('status_open') },
    { value: 'in_progress', label: t('status_in_progress') },
    // { value: 'resolved', label: t('status_resolved') },
    { value: 'closed', label: t('status_closed') }
  ];

  const priorityOptions = [
    { value: 'all', label: t('all_priorities') },
    { value: 'low', label: t('priority_low') },
    { value: 'medium', label: t('priority_medium') },
    { value: 'high', label: t('priority_high') }
  ];

  // Get categories based on user role
  const getCategoryOptions = () => {
    const categories = [{ value: 'all', label: t('all_categories') }];
    
    if (account?.role?.slug) {
      const supportCategories = supportCategoryTypes(account.role.slug);
      
      // Add all items from all groups
      supportCategories.forEach(group => {
        group.items.forEach(item => {
          categories.push({
            value: item.id,
            label: t(item.name)
          });
        });
      });
    }
    
    return categories;
  };

  const categoryOptions = getCategoryOptions();

  useEffect(() => {
    // Check URL parameters
    const params = new URLSearchParams(location.search);
    const ticketParam = params.get('ticket');
    const feedbackParam = params.get('feedback');
    
    if (ticketParam) {
      // If ticket parameter exists, fetch and open ticket details
      fetchTicketDetailsFromUrl(ticketParam, feedbackParam);
    } else {
      // Otherwise, fetch tickets list
      fetchTickets();
    }
  }, [location.search]);

  const fetchTicketDetailsFromUrl = async (ticketId: string, feedbackParam: string | null) => {
    try {
      setLoading(true);
      const resp = await instance.get(`support/tickets/${ticketId}`) as typeResponse;
      if ((resp as any).status === 'success') {
        const ticketData = (resp as any).data;
        setSelectedTicket(ticketData);
        
        // Check if we should show feedback
        if (feedbackParam !== null && ticketData.status === 'closed' && !ticketData.rating) {
          setShowFeedback(true);
        }
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'primary';
      case 'in_progress': return 'warning';
      case 'resolved': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return '#4caf50';
      case 'medium': return '#ff9800';
      case 'high': return '#f44336';
      case 'urgent': return '#9c27b0';
      default: return '#757575';
    }
  };

  const buildQueryParams = (page = 1, currentFilters = filters) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', '10');

    if (currentFilters.status !== 'all') {
      params.append('status', currentFilters.status);
    }
    if (currentFilters.priority !== 'all') {
      params.append('priority', currentFilters.priority);
    }
    if (currentFilters.category !== 'all') {
      params.append('category', currentFilters.category);
    }
    if (currentFilters.search.trim()) {
      params.append('search', currentFilters.search.trim());
    }

    return params.toString();
  };

  const fetchTickets = async (page = 1) => {
    try {
      setLoading(true);
      const queryParams = buildQueryParams(page);
      const resp = await instance.get(`support/tickets?${queryParams}`) as typeResponse;
      if ((resp as any).status === 'success') {
        setTickets((resp as any).data.tickets);
        setTicketsTotal((resp as any).data.pagination.total_items);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetails = async (ticketId: string) => {
    try {
      setLoading(true);
      const resp = await instance.get(`support/tickets/${ticketId}`) as typeResponse;
      if ((resp as any).status === 'success') {
        setSelectedTicket((resp as any).data);
        setShowFeedback(false); // Don't show feedback when opening from list
      }
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterKey: keyof TicketFilters, value: string) => {
    const newFilters = { ...filters, [filterKey]: value };
    setFilters(newFilters);
  };

  const applyFilters = () => {
    setTicketsPage(1);
    fetchTickets(1);
  };

  const clearFilters = () => {
    const clearedFilters = {
      status: 'all',
      priority: 'all',
      category: 'all',
      search: ''
    };
    setFilters(clearedFilters);
    setTicketsPage(1);
    // Fetch with cleared filters
    setTimeout(() => {
      const queryParams = buildQueryParams(1, clearedFilters);
      setLoading(true);
      instance.get(`support/tickets?${queryParams}`)
        .then((resp: any) => {
          if (resp.status === 'success') {
            setTickets(resp.data.tickets);
            setTicketsTotal(resp.data.pagination.total_items);
          }
        })
        .catch((error) => {
          console.error('Error fetching tickets:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }, 0);
  };

  const hasActiveFilters = () => {
    return filters.status !== 'all' || 
           filters.priority !== 'all' || 
           filters.category !== 'all' || 
           filters.search.trim() !== '';
  };

  const handleCloseTicketDetails = () => {
    setSelectedTicket(null);
    setShowFeedback(false);
    // Remove URL parameters
    navigate(location.pathname, { replace: true });
    // Refresh tickets list
    fetchTickets(ticketsPage);
  };

  return (
    <>
      {/* Filter Section */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: showFilters ? 2 : 0 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList />
            {t('filters')}
            {hasActiveFilters() && (
              <Chip 
                label={t('active')} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            )}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setShowFilters(!showFilters)}
            startIcon={<FilterList />}
          >
            {showFilters ? t('hide_filters') : t('show_filters')}
          </Button>
        </Box>

        {showFilters && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label={t('search')}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                placeholder={t('search_tickets_placeholder')}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('status')}</InputLabel>
                <Select
                  value={filters.status}
                  label={t('status')}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('priority')}</InputLabel>
                <Select
                  value={filters.priority}
                  label={t('priority')}
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                >
                  {priorityOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('category')}</InputLabel>
                <Select
                  value={filters.category}
                  label={t('category')}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                >
                  {categoryOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1, height: '40px' }}>
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  sx={{ flex: 1 }}
                >
                  {t('apply_filters')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  startIcon={<Clear />}
                  disabled={!hasActiveFilters()}
                >
                  {t('clear')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Results Summary */}
      {hasActiveFilters() && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('showing_filtered_results', { total: ticketsTotal })}
          </Typography>
        </Box>
      )}

      {/* Tickets Table */}
      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 600 }}>{t('ticket_id')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('subject')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('status')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('created')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('last_updated')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('messages')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography>{t('loading')}</Typography>
                </TableCell>
              </TableRow>
            ) : tickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {hasActiveFilters() ? t('no_tickets_found_with_filters') : t('no_tickets_found')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              tickets.map((ticket) => {
                const ticketSubject = getSupportCategoryItemById(ticket.subject, account?.role.slug || '');
                return (
                  <TableRow key={ticket.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', maxWidth: 300 }}>
                        {ticket.ticket_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {ticketSubject && t(ticketSubject.name)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`status_${ticket.status}`)}
                        size="small"
                        color={getStatusColor(ticket.status) as any}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(ticket.created_at, true)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(ticket.updated_at, true)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{ticket.messages_count}</Typography>
                        {ticket.last_message_from === 'admin' && (
                          <Tooltip title={t('admin_replied')}>
                            <AdminPanelSettings color="primary" sx={{ fontSize: 16 }} />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => fetchTicketDetails(ticket.id)}
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {ticketsTotal > 10 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(ticketsTotal / 10)}
            page={ticketsPage}
            onChange={(e, page) => {
              setTicketsPage(page);
              fetchTickets(page);
            }}
            color="primary"
          />
        </Box>
      )}

      {/* Ticket Details Modal */}
      {selectedTicket && (
        <SupportTicketDetails 
          open 
          selectedTicket={selectedTicket} 
          onClose={handleCloseTicketDetails}
          showFeedback={showFeedback || selectedTicket.status === 'closed' && !selectedTicket.rating}
          onFeedbackSubmit={() => {
            setShowFeedback(false);
            // Refresh ticket details after feedback submission
            if (selectedTicket) {
              fetchTicketDetails(selectedTicket.id);
            }
          }}
        />
      )}
    </>
  );
};

export default SupportTickersList;